import ChoixRenderer from '../VueQuestion/ChoixRenderer';
import { LETTRES, LETTRE_HEX, FONT_TITRE, QCM_GREEN, QCM_INK } from '../../utils/qcmTheme';

/**
 * Grille des choix A-D sur fond navy — la LETTRE est la vedette (les cartes
 * élèves sont en noir & blanc : c'est la lettre qui guide la rotation, la
 * couleur n'est qu'un accent, cohérent avec les barres du téléphone prof).
 * Tuiles sombres translucides, badge lettre coloré.
 * TOUJOURS 2 colonnes (même pour les choix-images : 2 rangées max, jamais
 * 4 rangées qui écraseraient l'énoncé) ; médias bornés à ~13vh dans un
 * encart blanc, zoomables au clic (ZoomableMedia) pour voir les détails.
 *
 * NI liseré fin autour, NI rail coloré à gauche (décision prof 2026-09-20) :
 * le rail répétait une couleur que la pastille de la lettre porte déjà à deux
 * centimètres de là, et la paire « carte arrondie + rail d'accent » est le tic
 * visuel des interfaces générées. Ce qui tenait la tuile, c'est son fond, pas
 * son contour : il monte donc de 7 à 10 % de blanc pour compenser le liseré
 * retiré, et la tuile se lit toujours comme un bloc sur le navy.
 *
 * `bonneLettre` n'est passée que par la revue de correction : elle éteint les
 * tuiles fausses et REMPLIT la bonne en vert. Sans elle — le cas du quizz en
 * cours — la grille est identique au bit près à ce qu'elle a toujours été.
 *
 * Fond plein et non un contour (décision prof 2026-09-20) : un liseré de 2 px
 * et un halo se délavent sur un vidéoprojecteur, la classe ne voyait pas quelle
 * tuile était la bonne. Un aplat se lit du fond de la salle. Le texte passe
 * alors en encre sombre, une fois blanche sur ce vert il tomberait à 2,3 de
 * contraste ; la pastille de la lettre s'inverse pour la même raison, blanche
 * avec sa lettre en vert, sinon le bleu ou l'orange de la lettre jurerait sur
 * l'aplat.
 */
const ChoicesGrid = ({ choix = [], bonneLettre = null }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 px-9 pt-3 flex-shrink-0 [&_img]:max-h-[13vh] [&_img]:object-contain [&_svg]:max-h-[13vh]">
    {choix.map((c, i) => {
      const lettre = LETTRES[i];
      const elementRiche = typeof c === 'object' && c !== null;
      const estBonne = bonneLettre != null && lettre === bonneLettre;
      const eteinte = bonneLettre != null && !estBonne;
      return (
        <div
          key={lettre}
          data-bonne-reponse={estBonne ? 'oui' : undefined}
          className={`proj-tile-pop flex items-center gap-3.5 px-4 py-3 rounded-2xl${estBonne ? '' : ' bg-white/10'}`}
          style={{
            background: estBonne ? QCM_GREEN : undefined,
            animationDelay: `${0.15 + i * 0.08}s`,
            opacity: eteinte ? 0.4 : 1,
            boxShadow: estBonne ? '0 0 34px rgba(34,181,115,.55)' : undefined,
          }}
        >
          <span
            className="flex items-center justify-center rounded-xl font-black flex-none shadow-lg"
            style={{
              width: 'clamp(34px, 3.2vw, 46px)',
              height: 'clamp(34px, 3.2vw, 46px)',
              fontSize: 'clamp(17px, 1.9vw, 24px)',
              background: estBonne ? '#ffffff' : LETTRE_HEX[lettre],
              color: estBonne ? QCM_GREEN : '#ffffff',
              fontFamily: FONT_TITRE,
            }}
          >
            {lettre}
          </span>
          {elementRiche ? (
            <span className="min-w-0 flex-1 rounded-xl bg-white p-1.5 flex items-center justify-center overflow-hidden">
              <ChoixRenderer choix={c} light compact className="font-semibold text-gray-800" />
            </span>
          ) : (
            <span
              className="min-w-0 font-bold"
              style={{ fontSize: 'clamp(15px, 1.8vw, 22px)', color: estBonne ? QCM_INK : '#ffffff' }}
            >
              <ChoixRenderer choix={c} className="font-bold [&_.katex]:text-inherit" />
            </span>
          )}
        </div>
      );
    })}
  </div>
);

export default ChoicesGrid;
